import {useEffect} from 'react';
import {
  Conversation,
  ConversationMessageAdded,
} from '@stripe-internal/conversations-client';
import {useMessagesTopAnchored} from '../helpers/useMessagesTopAnchored';
import {smoothScrollToWithBezier} from '../helpers/smoothScrollTo';

const scrollToTargetValue = 0;
const scrollToDuration = 800;
const scrollToEasingCurve = [0.16, 1, 0.3, 1]; // cubic-bezier values

export function MessageScroller({
  conversation,
  logRef,
  contentRef,
}: {
  conversation: Conversation;
  logRef: any;
  contentRef: any;
}) {
  const {shouldTopAnchor, isTopAnchoredEnabled} =
    useMessagesTopAnchored(conversation);

  // This effect performs the "scroll" animation to move the message
  // to the top of the screen. It only does this with a few types of
  // messages, such a user-sent text message.
  useEffect(() => {
    if (!shouldTopAnchor) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;

    const smoothScrollToTop = () => {
      smoothScrollToWithBezier(
        logRef.current,
        scrollToTargetValue,
        scrollToDuration,
        ...scrollToEasingCurve,
      );
    };
    // Note: the "initial" prompt from the user that the widget opens
    // with doesn't come through here because it's a message that
    // already exists. That's a good thing! We don't want to scroll
    // down when the widget opens because we show a greeting message
    // that we don't want to hide. All future messages will scrolll
    // down.
    const sub = conversation.eventsObservable().subscribe((e) => {
      if (
        e.name === 'conversation-message-added' &&
        e.message?.type === 'text-conversation-message'
      ) {
        const addedEvent = e as ConversationMessageAdded;

        if (addedEvent.participantId === conversation.user.id) {
          // New user message, scroll to it
          clearTimeout(timer);
          timer = setTimeout(smoothScrollToTop, 50);
        }
      } else if (
        e.name === 'conversation-option-selected' ||
        e.name === 'conversation-trigger-scroll-to-top'
      ) {
        clearTimeout(timer);
        timer = setTimeout(smoothScrollToTop, 50);
      }
    });

    return () => {
      clearTimeout(timer);
      sub.unsubscribe();
    };
  }, [conversation, shouldTopAnchor, logRef]);

  // This effect makes sure the CSS variable stays up to date with the
  // height of the the log. We need this when calculating the required
  // extra space so we can scroll messages to the top.
  useEffect(() => {
    if (!isTopAnchoredEnabled) {
      return;
    }

    const obs = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const {height} = entry.contentRect;

        logRef.current?.style.setProperty('--log-height', `${height}px`);
      });
    });

    obs.observe(logRef.current);

    return () => {
      obs.disconnect();
    };
  }, [isTopAnchoredEnabled, logRef]);

  // This effect implements a type of "scroll locking". Because of how
  // we implement the log using a flex item with `column-reverse`,
  // a "scroll" of 0 is at the bottom of the element. New content will
  // immediately appear at the bottom. We want to "lock" the scrolling
  // to the message that we moved up so new content appears below it.
  // To lock it we track the height with a resize observer and change
  // the `scrollTop` to avoid any visual changes
  useEffect(() => {
    if (!shouldTopAnchor) {
      return;
    }

    let prevHeight: number | null = null;

    const obs = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const {height} = entry.contentRect;

        if (prevHeight != null) {
          const diff = height - prevHeight;

          logRef.current.scrollTop -= diff;
        }

        prevHeight = height;
      });
    });

    obs.observe(contentRef.current);

    return () => {
      obs.disconnect();
    };
  }, [shouldTopAnchor, contentRef, logRef]);

  return null;
}
